import { useTranslation } from "react-i18next";
import { useEffect, useRef } from "react";
import { Check, ChevronLeft, ChevronRight } from "lucide-react";
import type { ManualBodyStep } from "@arc/contracts";
import { Button } from "@/components/ui/button";
import { StepInteractive } from "./StepInteractive.js";
import { StepMedia } from "./StepMedia.js";

/** Label KEYS per step type. The map is module-level and cannot call a hook; the RECORD keys are the wire
 *  contract's own type tokens and must stay as they are. */
const TYPE_LABEL_KEY = {
  regular: "viewer.stepTypeRegular",
  question: "viewer.question",
  checklist: "viewer.checklist",
  question_with_score: "viewer.scoredQuestion",
  show_score: "viewer.score",
} as const satisfies Record<ManualBodyStep["type"], string>;

/**
 * A single step in the viewer (ADR-002 Phase 5 — the basic display: name + instruction text + prev/next). The full typed
 * player (media, question answers, checklist items, score) lands with Phase 6; `type` already tells the viewer which.
 * a11y: focus moves to the heading on step change + an `aria-live` announcement for screen readers.
 */
export function StepView({
  step,
  index,
  total,
  onPrev,
  onNext,
  onFinish,
}: {
  step: ManualBodyStep;
  index: number;
  total: number;
  onPrev?: () => void;
  onNext?: () => void;
  onFinish?: () => void;
}) {
  const { t } = useTranslation();
  const headingRef = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    headingRef.current?.focus();
  }, [step.id]);
  return (
    <article className="mx-auto flex h-full max-w-2xl flex-col gap-4 p-6">
      <p className="sr-only" aria-live="polite">
        {t("viewer.stepXofY", { index: index + 1, total })}
      </p>
      <div className="flex items-center gap-2">
        <span className="bg-muted text-muted-foreground rounded px-1.5 py-0.5 text-xs font-medium">{t(TYPE_LABEL_KEY[step.type])}</span>
        <span className="text-muted-foreground text-xs tabular-nums">
          {index + 1} / {total}
        </span>
      </div>
      <h2 ref={headingRef} tabIndex={-1} className="text-lg font-semibold outline-none">
        {step.name}
      </h2>
      {step.text ? (
        // Server-sanitized on read (ADR-013 / panel #6) — safe to render as formatted HTML.
        <div
          className="text-sm leading-relaxed [&_ol]:mb-2 [&_ol]:list-decimal [&_ol]:pl-5 [&_p]:mb-2 [&_ul]:mb-2 [&_ul]:list-disc [&_ul]:pl-5"
          dangerouslySetInnerHTML={{ __html: step.text }}
        />
      ) : (
        <p className="text-muted-foreground text-sm">{t("viewer.noInstructions")}</p>
      )}
      {step.media ? <StepMedia media={step.media} name={step.name} /> : null}
      <StepInteractive step={step} />
      <div className="mt-auto flex justify-between gap-2 pt-4">
        <Button variant="outline" onPress={onPrev} isDisabled={!onPrev} className="dark:border-border">
          <ChevronLeft aria-hidden="true" />
          {t("viewer.back")}
        </Button>
        {onFinish ? (
          <Button variant="accent" onPress={onFinish}>
            {t("viewer.finish")}
            <Check aria-hidden="true" />
          </Button>
        ) : (
          // Weiter leads to more content → orange `accent` (owner 2026-10-22), forward icon on the right.
          <Button variant="accent" onPress={onNext} isDisabled={!onNext}>
            {t("viewer.next")}
            <ChevronRight aria-hidden="true" />
          </Button>
        )}
      </div>
    </article>
  );
}

/** A step's attachment (ADR-002 Phase 6): image / video / audio inline; model3d + unknown types get a labelled
 *  placeholder (the AR/3D viewer is #63). `url` may be null when storage is unconfigured — the element degrades. */
